import React, { useState, useEffect } from 'react';
import { getGallery, getGalleryCategories, getGalleryByCategory } from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Camera } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const GalleryPage = () => {
  const [images, setImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  const getImageUrl = (img) => {
    const p = img.storage_path || img.image_path;
    if (!p) return null;
    if (p.startsWith('http')) return p;
    return `${API}/api/files/${p}`;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [imgRes, catRes] = await Promise.all([getGallery(), getGalleryCategories()]);
        setImages(imgRes.data);
        setCategories(catRes.data);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleCategoryFilter = async (catId) => {
    setLoading(true);
    setActiveCategory(catId);
    try {
      if (catId) {
        const res = await getGalleryByCategory(catId);
        setImages(res.data);
      } else {
        const res = await getGallery();
        setImages(res.data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="gallery-page">
      <section className="section-padding bg-surface">
        <div className="container-max">
          <h1 className="text-4xl md:text-5xl font-heading font-bold uppercase tracking-tighter mb-4">
            <span className="text-primary">Galerie</span>
          </h1>
          <p className="text-text-secondary max-w-2xl">
            Eindrücke aus dem Vereinsleben, Turnieren und Trainings.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      {categories.length > 0 && (
        <section className="py-6 px-4 border-b border-white/10">
          <div className="container-max flex flex-wrap gap-2">
            <Button
              variant={activeCategory === null ? 'default' : 'outline'}
              className={activeCategory === null ? 'bg-primary' : 'border-white/20'}
              onClick={() => handleCategoryFilter(null)}
              data-testid="gallery-filter-all"
            >
              Alle
            </Button>
            {categories.map(cat => (
              <Button
                key={cat.category_id}
                variant={activeCategory === cat.category_id ? 'default' : 'outline'}
                className={activeCategory === cat.category_id ? 'bg-primary' : 'border-white/20'}
                onClick={() => handleCategoryFilter(cat.category_id)}
                data-testid={`gallery-filter-${cat.category_id}`}
              >
                {cat.name}
              </Button>
            ))}
          </div>
        </section>
      )}

      <section className="section-padding">
        <div className="container-max">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(i => <div key={i} className="aspect-square bg-surface animate-pulse rounded-sm" />)}
            </div>
          ) : images.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {images.map(image => {
                const url = getImageUrl(image);
                return (
                  <Card key={image.image_id} className="card-surface overflow-hidden cursor-pointer group" onClick={() => setSelectedImage(image)}>
                    <div className="aspect-square relative">
                      {url ? (
                        <img src={url} alt={image.title || ''} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110" loading="lazy" />
                      ) : (
                        <div className="w-full h-full bg-surface-elevated flex items-center justify-center"><Camera className="w-12 h-12 text-text-secondary" /></div>
                      )}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        {image.title && <p className="text-white font-medium text-sm px-4 text-center">{image.title}</p>}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16 card-surface">
              <Camera className="w-16 h-16 text-text-secondary mx-auto mb-4" />
              <p className="text-text-secondary">Noch keine Bilder in der Galerie.</p>
            </div>
          )}
        </div>
      </section>

      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl bg-black/90 border-white/10 p-2">
          {selectedImage && (
            <div className="relative">
              <img src={getImageUrl(selectedImage)} alt={selectedImage.title || ''} className="w-full h-auto max-h-[80vh] object-contain" />
              {selectedImage.title && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                  <p className="text-white font-heading font-bold uppercase">{selectedImage.title}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GalleryPage;
